import type { Milestone } from "../types/order";

export default function MilestoneTracker({
  milestones,
}: {
  milestones?: Milestone[];
}) {
  // Check if order is in terminal state
  const isTerminalState = milestones?.some(
    (m) =>
      (m.status === "Fulfilled" || m.status === "Active") &&
      (m.stage === "Order Closed" ||
        m.stage === "Refund Initiated" ||
        m.stage === "Refund Completed")
  );

  const sortedMilestones = [...(milestones || [])]
    // Remove duplicate milestones by milestone_id
    .filter((milestone, index, arr) => {
      return index === arr.findIndex((m) => m.milestone_id === milestone.milestone_id);
    })
    // Filter out Inactive milestones that share priority with another
    .filter((milestone, index, arr) => {
      const hasSamePriority = arr.some(
        (m, i) => i !== index && m.priority === milestone.priority
      );
      return !(milestone.status === "Inactive" && hasSamePriority);
    })
    // Filter out incomplete milestones if in terminal state
    .filter((milestone) => {
      if (!isTerminalState) return true;

      // Always show terminal milestones themselves
      const isTerminalMilestone =
        milestone.stage === "Order Closed" ||
        milestone.stage === "Refund Initiated" ||
        milestone.stage === "Refund Completed";

      if (isTerminalMilestone) return true;

      // In terminal state, only show Fulfilled milestones
      return milestone.status === "Fulfilled";
    })
    .sort((a, b) => {
      // Primary sort: Group by status (Fulfilled first, then Active/Inactive)
      const aStatusWeight = a.status === "Fulfilled" ? 0 : 1;
      const bStatusWeight = b.status === "Fulfilled" ? 0 : 1;

      if (aStatusWeight !== bStatusWeight) {
        return aStatusWeight - bStatusWeight;
      }

      // Secondary sort: Within each status group, sort by priority
      return a.priority - b.priority;
    });

  return (
    <div className="flex flex-col w-full h-full">
      {sortedMilestones.map((milestone, idx) => {
        // Determine milestone status
        const isCompleted = milestone.status === "Fulfilled";
        const isActive = milestone.status === "Active";
        const isRejected = milestone.stage === "Order Rejected";
        const isCancelled = milestone.stage === "Order Cancelled";

        // Color logic
        const dotColor = isCancelled
          ? "border-red-500 bg-red-500" // red dot for cancelled
          : isRejected
            ? "border-red-500 bg-red-500"
            : isCompleted
              ? "border-[#00A82D] bg-[#00A82D]"
              : isActive
                ? "border-[#083990] bg-[#083990]"
                : "border-[#626262] bg-[#626262]";

        const lineColor = isRejected
          ? "bg-[#ee4236e0]"
          : isCompleted
          ? "border-[#00A82D]"
          : isActive
          ? "border-[#083990]"
          : "bg-gray-300";

        // Calculate line height based on content
        const hasSubStages =
          milestone.sub_stages && milestone.sub_stages.length > 0;

        // const colors = generic_order_status_options.find(
        //     (status) => status.label.toLowerCase() === milestone?.stage.toLowerCase()
        //   );

        return (
          <div
            key={milestone.milestone_id}
            className="flex items-start gap-3 relative"
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-4 h-4 rounded-full border-2 ${dotColor} z-10`}
              ></div>
              {idx < sortedMilestones.length - 1 && (
                <div
                  className={` border-[1.5px] ${lineColor} flex-grow`}
                  style={{
                    height: hasSubStages ? "calc(100% - 8px)" : "50px",
                    marginTop: -8,
                    position: hasSubStages ? "absolute" : "relative",
                    top: hasSubStages ? "16px" : "auto",
                    bottom: hasSubStages ? "0" : "auto",
                  }}
                ></div>
              )}
            </div>
            <div className="flex-1">
              <div
                // style={{
                //   color: isRejected ? '#ef4444' : colors?.value || '#626262',
                // }}
                className={`font-medium ${
                  isRejected
                    ? "text-red-500"
                    : isCompleted || isActive
                    ? "text-[#083990]"
                    : "text-gray-400"
                }`}
              >
                {milestone.stage}
              </div>

              {/* Latest timestamp for the milestone */}
              {/* <div className="text-xs text-gray-400 mb-1">
                {milestone.sub_stages && milestone.sub_stages[0]?.created_at
                  ? new Date(milestone.sub_stages[0].created_at).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : ""}
              </div> */}

              {/* Display all sub-stages */}
              {/* {hasSubStages && (
                <div className="ml-2 mt-2 space-y-2">
                  {milestone.sub_stages.map((subStage, subIdx) => (

                  ))}
                </div>
              )} */}
              {hasSubStages && (
                <div className="ml-2 my-2 space-y-2">
                  <div className="py-1">
                    {/* <div
                      className={`text-sm font-medium ${milestone?.sub_stages?.at(-1)?.status === "Fulfilled"
                          ? "text-green-600"
                          : milestone?.sub_stages?.at(-1)?.status === "Active"
                            ? "text-[#083990]"
                            : "text-gray-400"
                        }`}
                    >
                      {milestone?.sub_stages?.at(-1)?.sub_stage_name}
                    </div> */}

                    {/* <div className={`text-sm font-medium text-gray-400`}>
                      {milestone?.sub_stages?.at(-1)?.sub_stage_name}
                    </div> */}                    
                    {
                      (milestone.stage?.toLowerCase() !== milestone?.sub_stages?.at(-1)?.description?.toLowerCase() && (
                        milestone?.sub_stages?.at(-1)?.sub_stage_name?.toLowerCase() !== milestone.stage?.toLowerCase()
                      )) ?
                        milestone?.sub_stages?.at(-1) &&
                        <div className="text-sm text-gray-600">
                          {milestone?.sub_stages?.at(-1)?.description ||
                            milestone?.sub_stages?.at(-1)?.sub_stage_name}
                        </div>
                        :
                        null
                    }
                    <div className="text-xs text-gray-400">
                      {milestone?.sub_stages?.at(-1)?.created_at
                        ? new Date(
                            milestone?.sub_stages?.at(-1)?.created_at as number
                          ).toLocaleString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : ""}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
